import Button from '@carvana/ds-button';
import { SlateLogo } from '@carvana/ds-icons';
import { datadogRum } from '@datadog/browser-rum';
import React, { ErrorInfo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import footerStyles from '|/app/footer/slate-footer/slate-footer.module.css';
import PoweredByCarvana from '|/app/header/slate-header/powered-by-carvana';
import SlateBrandLockup from '|/app/header/slate-header/slate-brand-lockup';
import hdrStyles from '|/app/header/slate-header/slate-header.module.css';
import SlateLogoDesktop from '|/app/header/slate-header/slate-logo-desktop';
import { resolveBrandFromEnvironment } from '|/brand';
import Link from '|/components/carvana-ds/link';
import { getCurrentStepName } from '|/helpers/current-step-tracker';
import { isNativeWebview, isAndroidNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useEventHelper from '|/hooks/use-event-helper';
import useReloadPurchaseProcess from '|/hooks/use-reload-purchase-process';
import styles from './error.module.css';

const Error: React.FC = () => {
  const reloadPurchaseProcess = useReloadPurchaseProcess();
  const { sendEvent } = useEventHelper();
  const location = useLocation();
  const isSlate = resolveBrandFromEnvironment() === 'slate';

  // Redundant under index.tsx (module scope already themed the page) but load-bearing
  // for index-step-only.tsx, which never writes body[data-theme].
  useEffect(() => {
    if (isSlate) {
      document.body.dataset.theme = 'slate';
    }
  }, [isSlate]);

  useEffect(() => {
    sendEvent('pp_load_error');
    try {
      const stepName =
        getCurrentStepName() ?? location?.pathname?.split('/steps/')?.[1]?.split('/')?.[0] ?? '';
      datadogRum.addAction('step_load_error', {
        url: location.pathname,
        stepName
      });
    } catch (e) {}
    // Only fire once when error page mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEvent]);

  useEffect(() => {
    if (isNativeWebview()) {
      const stepName =
        getCurrentStepName() ?? location?.pathname?.split('/steps/')?.[1]?.split('/')?.[0] ?? '';
      sendNativeEvent({
        event: MobileWebviewEvents.SPEED_BUMP,
        payload: { currentStep: location.pathname, stepName },
        source: 'purchase'
      });
    }
  }, [location.pathname]);

  const handleTryAgain = () => {
    reloadPurchaseProcess();
  };

  const handleSupportClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isNativeWebview()) {
      e.preventDefault();
      const event = isAndroidNativeWebview()
        ? MobileWebviewEvents.OPEN_SEBASTIAN
        : MobileWebviewEvents.OPEN_SUPPORT;
      sendNativeEvent({
        event: event,
        payload: { currentStep: location.pathname },
        source: 'purchase'
      });
    }
  };

  const handlePhoneClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isNativeWebview()) {
      e.preventDefault();
      sendNativeEvent({
        event: MobileWebviewEvents.CONTACT_AGENT,
        payload: { phone: '1-888-415-0755' },
        source: 'purchase'
      });
    }
  };

  return (
    <div className={styles.page} data-analytics-id="error-page">
      {isSlate && (
        <header className={hdrStyles.header}>
          <div className={hdrStyles.brand} data-testid="slate-error-brand">
            <span className={hdrStyles.logoLink}>
              <SlateBrandLockup className={hdrStyles.brandLockup} />
              <SlateLogoDesktop className={hdrStyles.slateLogoDesktop} />
            </span>
            <PoweredByCarvana className={hdrStyles.poweredByCarvana} />
          </div>
        </header>
      )}

      <main className={styles.main}>
        <div className={styles.contentLockup}>
          <div className={styles.header}>
            <div className={styles.heading}>
              <h1 className={styles.title}>We&apos;ve hit a speed bump</h1>
            </div>

            {isSlate ? (
              <p className={styles.text}>
                We&apos;re sorry, something went wrong. Please try again or call{' '}
                <span className={styles.noWrap}>
                  <Link
                    href="tel:+18884150755"
                    variant="standalone"
                    priority="primary"
                    size="sm"
                    data-analytics-id="pp_oopsie_daisy_phone"
                    onClick={handlePhoneClick}
                  >
                    1-888-415-0755
                  </Link>
                </span>{' '}
                for more help
              </p>
            ) : (
              <p className={styles.text}>
                We&apos;re sorry, something went wrong. Please visit our{' '}
                <Link
                  href={`${process.env.BASE_URL}/help`}
                  variant="standalone"
                  priority="primary"
                  size="sm"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-analytics-id="pp_oopsie_daisy_support_center"
                  onClick={handleSupportClick}
                >
                  Support Center
                </Link>{' '}
                or call{' '}
                <span className={styles.noWrap}>
                  <Link
                    href="tel:+18884150755"
                    variant="standalone"
                    priority="primary"
                    size="sm"
                    data-analytics-id="pp_oopsie_daisy_phone"
                    onClick={handlePhoneClick}
                  >
                    1-888-415-0755
                  </Link>
                </span>{' '}
                for more help.
              </p>
            )}
          </div>

          <Button
            variant="primary"
            size="md"
            className={styles.tryAgainButton}
            onClick={handleTryAgain}
            data-analytics-id="pp_oopsie_daisy_retry"
          >
            Try again
          </Button>
        </div>
      </main>

      {isSlate && (
        <footer className={footerStyles.footer} data-testid="slate-error-footer">
          <SlateLogo className={footerStyles.logo} color="currentColor" />
          <p className={footerStyles.copyright}>
            Copyright © {new Date().getFullYear()} Carvana, LLC. All Rights Reserved.
          </p>
        </footer>
      )}
    </div>
  );
};

// ErrorOverlay component
interface ErrorOverlayProps {
  error?: Error | string;
  visible: boolean;
  onDismiss: () => void;
  errorInfo?: ErrorInfo;
}

export const ErrorOverlay: React.FC<ErrorOverlayProps> = ({
  error,
  visible,
  onDismiss,
  errorInfo
}) => {
  if (!error) return null;

  return (
    <div className={`${styles.overlayContainer} ${visible ? styles.visible : ''}`}>
      <div className={styles.overlayHeader}>
        <h2 className={styles.overlayTitle}>Error</h2>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onDismiss}
          aria-label="Dismiss error"
          data-analytics-id="pp_error_overlay_dismiss"
        >
          ×
        </button>
      </div>
      <div className={styles.overlayContent}>
        <div className={styles.errorMessage}>{error?.toString()}</div>
        <pre className={styles.overlayErrorStack}>{error instanceof Error ? error.stack : ''}</pre>
        <pre className={styles.overlayErrorStack}>{errorInfo?.componentStack}</pre>
      </div>
    </div>
  );
};

// Hook to use the ErrorOverlay
export const useErrorOverlay = () => {
  const [overlayError, setOverlayError] = useState<Error | string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const showError = (error: Error | string) => {
    setOverlayError(error);
    setIsVisible(true);
  };

  const dismissError = () => {
    setIsVisible(false);
  };

  const ErrorOverlayComponent = () => (
    <ErrorOverlay error={overlayError ?? undefined} visible={isVisible} onDismiss={dismissError} />
  );

  return {
    showError,
    dismissError,
    ErrorOverlayComponent
  };
};

export default Error;
